import * as React from 'react';

/**
 * Grows an iframe to match its content's natural height instead of scrolling
 * internally. CSS alone can't auto-size an iframe to its (cross-document)
 * content, so this measures the rendered height directly and writes it back
 * as an inline style — re-measuring on load, one frame later (some content
 * reflows a tick after `load`, e.g. image decode finishing), and on any
 * subsequent resize of the iframe's own body.
 *
 * Spread the returned props onto the iframe element.
 */
export const useAutoResizeIframe = () => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);

  const resize = React.useCallback<() => void>(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc?.body) {
      return;
    }
    // Max across body/documentElement scrollHeight *and* offsetHeight: a
    // single metric can under-report depending on whether the child
    // document's margins collapse into `documentElement` or not.
    const height = Math.max(
      doc.body.scrollHeight,
      doc.body.offsetHeight,
      doc.documentElement.scrollHeight,
      doc.documentElement.offsetHeight
    );
    if (height > 0) {
      iframe.style.height = `${height}px`;
    }
  }, []);

  const handleLoad = React.useCallback<() => void>(() => {
    resize();
    window.requestAnimationFrame(resize);

    const body = iframeRef.current?.contentDocument?.body;
    if (!body || typeof ResizeObserver === 'undefined') {
      return;
    }
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(resize);
    resizeObserverRef.current.observe(body);
  }, [resize]);

  React.useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

  return { ref: iframeRef, onLoad: handleLoad };
};
